import { Component, Fragment, h } from 'preact';
import { Id, Maybe, RosterEntry } from '../../../common/data.js';
import {
	addDesignToCart,
	AddDesignToCartResult,
	calculateDesignPrice,
	calculateUploadColors,
	getColorPresetsForProfile,
	listOtherApparelProducts,
	saveDesign,
	SaveDesignResult,
	startSocialAuth,
	updateCartItems,
	UpdateCartItemsResult,
} from '../../lib/api.js';
import { DesignPreview } from '../preview.js';
import { BuilderDesktopSettings, BuilderMobileSettings } from './settings.js';
import { ColorTuple, initColorPreset, MainColorGroup, mainColorGroups } from '../../../common/data/colors.js';
import {
	apply,
	ensure,
	findMatch,
	formatPrice,
	isIn,
	logRejection,
	makeIndex,
	maybe,
	maybePipe,
	pick,
	throttle,
} from '../../../common/util.js';
import { Icon } from '../icons.js';
import { RosterEditor, SizeOnlyRosterEditor } from '../roster.js';
import { ColorPicker } from '../colorpicker.js';
import { alertOnError, Popup, showAlert, showConfirm, showErrorAlert } from '../popup.js';
import { ArtworkBrowser } from './artwork.js';
import querystring from 'querystring';
import pako from 'pako';
import { makeDesignPreviewUrl } from '../../lib/images.js';
import { DesignManager } from '../../lib/design.js';
import { SizeChartTable } from '../size-chart-table.js';
import { getDesignRosterFields } from '../../../common/data/roster.js';
import { CartBlock } from './cart-block.js';
import { CartItem, DesignPriceBreakdown } from '../../../common/data/cart.js';
import { Context } from '../../../common/context.js';
import { classNames } from '../../lib/util.js';
import {
	Design,
	DesignPart,
	DesignParts,
	Font,
	getProductTemplateFromParts,
	ProductTemplate,
	ProductTemplateInventory,
	RosterPartType,
	rosterTextPartTypes,
	SavedDesign,
	TemplateCategory,
	textPartTypes,
	ThreadColor,
	upgradeRoster,
} from '../../../common/data/designs.js';
import {
	ComputedProduct,
	FullPublicProduct,
	Point,
	PublicProduct,
	WithFeatures,
} from '../../../common/data/products.js';
import { PublicFlow } from '../../../common/data/flows.js';
import { PublicDomain } from '../../../common/data/domains.js';
import { ContentExtras } from '../../../common/data/content.js';
import { AuthForm } from '../auth.js';

export const defaultMainColors: Record<string, ColorTuple> = {};
for (let i = 0; i < mainColorGroups.length; i++) {
	defaultMainColors[mainColorGroups[i]!] = initColorPreset[i]!;
}

export interface BuilderProps {
	context: Context;
	fonts: Font[];
	design?: Maybe<Design>;
	cartItem?: CartItem;
	sharedItem?: SavedDesign;
	product: FullPublicProduct;
	flow: PublicFlow;
	productTemplates: ProductTemplate[];
	productTemplateInventory: ProductTemplateInventory[];
	initProductTemplate: Maybe<Id<ProductTemplate>>;
	presentColors: MainColorGroup[];
	categories: TemplateCategory[];
	threadColors: Maybe<ThreadColor[]>;
	haveUser?: boolean;
	isAdmin?: boolean;
	logoInDesign: boolean;
	availableAuthServices: string[];
	domain: PublicDomain;
	extrasDescription: ContentExtras;
}

interface ProductSwitchEntry {
	product: ComputedProduct & WithFeatures;
	flow: PublicFlow;
	product_template: ProductTemplate;
	product_template_inventory: ProductTemplateInventory;
	match: boolean;
}

interface BuilderZoomAreaInit {
	side: string;
	part: DesignPart['id'];
	point: Point;
}
interface BuilderZoomArea extends BuilderZoomAreaInit {
	width: number;
	height: number;
	panX: number;
	panY: number;
}

interface BuilderState {

	mobile: boolean;
	settingsHidden?: boolean;
	fixedHeight: number;

	side: 'front' | 'back' | string;
	selectedPart?: Maybe<string>;
	invertDesignPreview?: boolean;
	templateCategory?: Maybe<TemplateCategory['id']>;

	dialog?: string;
	shareUrl?: string;
	priceBreakdown?: Maybe<DesignPriceBreakdown>;
	switchingProduct?: ProductSwitchEntry[];
	showAllProducts?: boolean;

	logoColors?: number[][];
	featuredColor?: number[];
	secondaryColor?: number[];

	colorPresets?: Maybe<ColorTuple[][]>;

	zoomArea?: Maybe<BuilderZoomArea>;

}

export class Builder extends Component<BuilderProps, BuilderState> {

	version = 7;

	doDrag?: ((cx: number, cy: number) => void) | undefined;
	endDrag?: (() => void) | undefined;

	previews: Record<string, DesignPreview> = {};
	designScrollArea: Maybe<HTMLElement>;

	headerHeight = document.querySelector('.header')!.getBoundingClientRect().height;
	manager!: DesignManager;
	experiments = new Set<string>();
	rosterUi: Maybe<RosterEditor | SizeOnlyRosterEditor>;

	constructor(props: BuilderProps) {

		super(props);

		this.state = {
			mobile: this.isMobile(),
			side: 'front',
			fixedHeight: window.innerHeight - this.headerHeight,
			dialog: undefined,
		};

		this.init();

		const endDrag = () => {
			if (this.endDrag) this.endDrag();
			if (this.doDrag) {
				this.doDrag = undefined;
				this.endDrag = undefined;
			}
		};
		window.addEventListener('mousemove', e => {
			if (!this.doDrag) return;
			e.preventDefault();
			this.doDrag(e.clientX, e.clientY);
		});
		window.addEventListener('mouseup', endDrag);
		window.addEventListener('touchmove', e => {
			if (!this.doDrag) return;
			e.preventDefault();
			this.doDrag(e.targetTouches[0]!.clientX, e.targetTouches[0]!.clientY);
		}, { passive: false });
		window.addEventListener('touchend', endDrag);

		window.addEventListener('resize', () => {
			const mobile = this.isMobile();
			if (mobile !== this.state.mobile) this.setState({ dialog: undefined });
			this.setState({
				mobile,
				fixedHeight: window.innerHeight - this.headerHeight,
			});
			this.updateZoomArea();
		}, { passive: true });

		ColorPicker.haveCopied = () => this.setState({}); // eww.

	}

	render(props: BuilderProps, state: BuilderState) {

		const sides = props.product.features?.sides;
		
		const sizesOnly = this.manager.state.isRosterFixedSized;
		const RosterUI = sizesOnly ? SizeOnlyRosterEditor : RosterEditor;
		const rosterFields = getDesignRosterFields(
			props.product,
			this.manager.state.parts,
			undefined,
			'allowed',
		);

		return <main className="builder" style={`height: ${state.fixedHeight}px; flex: 0 0 ${state.fixedHeight}px`}>

			<div className="builder__actions">
				<button
					className="builder__actions-button"
					onClick={() => this.saveDesign()}
				>
					<Icon name="star" />
					<span>Save</span>
				</button>
				<button className="builder__actions-button" onClick={() => this.showSharingPopup()}>
					<Icon name="share" />
					<span>Share</span>
				</button>
				{!state.mobile && <button className="builder__actions-button" onClick={() => this.savePreview()}>
					<Icon name="image-file" />
					<span>Download</span>
				</button>}
				<button className="builder__actions-button" onClick={() => this.maybeReset()}>
					<Icon name="undo" />
					<span>Reset</span>
				</button>
				<button className="builder__actions-button" onClick={() => this.showProductSwitch()}>
					<Icon name="copy" />
					<span>Collection</span>
				</button>
				<a href="/products" className="builder__actions-button builder__actions-button--unimportant">
					<Icon name="apparel" />
					<span>Products</span>
				</a>
				<span className="builder__actions-button builder__actions-button--dropdown">
					<Icon name="funnel" />
					<select onChange={async e => {
						const val = (e.target as HTMLSelectElement).value;
						this.manager.setProductTemplate(undefined);
						this.setState({ templateCategory: val === '-- All --' ? undefined : val });
					}}>
						<option selected={!!state.templateCategory}>-- All --</option>
						{props.categories.map(c => <option value={c.id} selected={state.templateCategory === c.id}>
							{c.name}
						</option>)}
					</select>
				</span>
				{state.mobile && [<div className="builder__actions-price">
					{maybe(state.priceBreakdown?.total, formatPrice)}
				</div>,
				props.product.features?.roster && <button className="builder__actions-button builder__actions-button--roster" onClick={() => this.setState({ dialog: 'roster' })}>
					<Icon name="team" />
					<span>Roster</span>
				</button>,
				<button className="builder__actions-button builder__actions-button--main" onClick={() => this.manager.state.productTemplate ? this.saveToCart() : showAlert('Please select a design first', 'warning')}>
					<Icon name="cart" />
					<span>{props.cartItem ? 'Update' : 'To cart'}</span>
				</button>]}
			</div>

			<Fragment>

				{!state.switchingProduct && <div key="design" className={apply('builder__design-column', c => classNames([
					c,
					`${c}--${state.invertDesignPreview ? 'inverted' : 'normal'}`,
					`${c}--${this.manager.state.productTemplate ? 'with-design' : 'no-design'}`,
				]))}>

					{this.manager.state.productTemplate && props.productTemplates.length > 1 && [
						<div
							className={`builder__floating-action builder__floating-action--designs builder__floating-action--with-roster`}
						>
							<button class="builder__floating-action-button" onClick={() => this.switchProductTemplate(-1)}>
								<Icon name="arrow-left" />
							</button>
							<button class={classNames('builder__floating-action-button', state.mobile && '--designs')} onClick={() => this.manager.setProductTemplate(undefined)}>
								<Icon name="two-grid" />
								{!state.mobile && <span>Change design</span>}
							</button>
							<button class="builder__floating-action-button" onClick={() => this.switchProductTemplate(1)}>
								<Icon name="arrow-right" />
							</button>
						</div>,
					]}

					{state.zoomArea && <div className="builder__floating-action builder__floating-action--zoom">
						<button class="builder__floating-action-button" onClick={this.resetZoom}>
							<Icon name="zoom-out" />
						</button>
					</div>}

					{state.mobile && (sides?.length || 0) > 1 && <div
						className="builder__floating-action builder__floating-action--side"
						onClick={this.toggleSide}
					>
						⇄
					</div>}

					{!this.manager.state.productTemplate && !state.mobile && (sides?.length || 0) > 1 && <div
						className={`builder__floating-action builder__floating-action--side builder__floating-action--side-bottom builder__floating-action--with-roster`}
					>
						<div
							className={`builder__floating-action-switch ${state.side == 'front' ? 'builder__floating-action-switch--right' : 'builder__floating-action-switch--left'}`}
							onClick={() => this.toggleSide()}
						>
							<div className="builder__floating-action-switch-inner">{state.side == 'front' ? 'Front' : 'Back'}</div>
						</div>
					</div>}
					{state.mobile && <div class="builder__floating-action builder__floating-action--hide-settings">
						<button class="builder__floating-action-button" onClick={() => this.setState({ settingsHidden: !state.settingsHidden })}>
							{state.settingsHidden ? <Icon name="arrow-up" /> : <Icon name="arrow-down" />}
							{state.settingsHidden === undefined && <span>Expand Preview</span>}
						</button>
					</div>}
					{!state.mobile && <div
						className={`builder__floating-action builder__floating-action--invert builder__floating-action--with-roster`}
					>
						<div
							className={`builder__floating-action-switch ${state.invertDesignPreview ? 'builder__floating-action-switch--right' : 'builder__floating-action-switch--left'}`}
							onClick={() => this.toggleInvert()}
						>
							<div className="builder__floating-action-switch-inner">{state.invertDesignPreview ? 'Light' : 'Dark'}</div>
						</div>
					</div>}

					{state.mobile && <div className="builder__floating-action builder__floating-action--mobile-invert" onClick={() => this.toggleInvert()} />}
					
					<div
						className={classNames(
							'builder__design-column-inner',
							!!state.zoomArea && '--zoom',
							'design-preview-container',
						)}
						style={{
							'--product-aspect-ratio': maybe(props.product.features?.imageSizes?.[0], ([w, h]) => (w / h).toFixed(3)) || 1,
						}}
						ref={it => this.designScrollArea = it}
						key={`pt-${!!this.manager.state.productTemplate}` /* force new element */}
					>
						{!this.manager.state.productTemplate && <>
							<DesignGrid
								side={state.side}
								parts={this.manager.state.partsSorted}
								productTemplates={maybe(
									state.templateCategory,
									tc => props.productTemplates.filter(d => d.template_info?.categories.includes(tc)),
								) || props.productTemplates}
								productTemplateInventory={props.productTemplateInventory}
								builder={this}
								onSelect={(t, i) => {
									if (maybe(i?.productSizesInStock, s => !s.length)) {
										showAlert(
											'This product is currently completely out of inventory. Please choose another product.',
											'warning',
										).catch(logRejection);
										return;
									}
									else if (i?.productSizesFromVendor && i.productSizesInStock && i.productSizesInStock.length < i.productSizesFromVendor.length) showAlert(
										'This product has missing inventory in certain sizes. Please use discretion making bulk purchases or adding it to your store.',
										'warning',
									);
									this.manager.setProductTemplate(t);
								}}
							/>
						</>}
						{this.manager.state.productTemplate && !state.zoomArea && <>
							<div
								className={classNames(
									'builder__design-preview',
									(sides?.length || 0) > 1 ? '--two-sided' : '--one-sided',
								)}
							>
								{sides?.map(side => <div
									class={`builder__design-preview-inner ${state.mobile && side != state.side
										? 'builder__design-preview-inner--hidden'
										: ''}`}
								>
									<DesignPreview
										product={props.product}
										flow={props.flow}
										parts={this.manager.state.parts}
										side={side}
										editable={true}
										builder={this}
										selectPart={this.selectPart}
										selected={state.selectedPart}
										ref={it => this.previews[side] = it}
									/>
								</div>)}
							</div>
							{!state.mobile && !sizesOnly && <RosterUI
								ref={it => this.rosterUi = it}
								roster={this.manager.state.roster}
								previewing={this.manager.state.previewingRoster}
								onChange={this.manager.updateRosterEntry}
								onDelete={this.manager.deleteRosterEntry}
								onPreview={this.manager.previewRosterEntry}
								product={props.product}
								fields={rosterFields}
								showPart={(type: RosterPartType) => this.rosterEditorShowPart(type)}
								replace={this.manager.replaceRoster}
								showSizeCharts={() => this.setState({ dialog: 'sizeCharts' })}
								sizesInStock={this.manager.state.productTemplateInventory?.productSizesInStock}
								sizesOfferedByVendor={this.manager.state.productTemplateInventory?.productSizesFromVendor}
							/>}
						</>}
						{this.manager.state.productTemplate && maybe(state.zoomArea, za => <div
							class="builder__design-zoom"
							style={{
								width: `${za.width}px`,
								height: `${za.height}px`,
								transform: `translate(${za.panX}px, ${za.panY}px)`,
							}}
						>
							<DesignPreview
								product={props.product}
								flow={props.flow}
								parts={this.manager.state.parts}
								side={za.side}
								editable={true}
								builder={this}
								selectPart={this.selectPart}
								selected={state.selectedPart}
								ref={it => this.previews[za.side] = it}
								forceInView={true}
							/>
						</div>)}
					</div>

				</div>}

				{state.switchingProduct && <div key="product-switch" className="builder__product-switch">
					<div className="builder__product-switch-inner design-preview-container">

						<ProductSwitchGrid data={(state.showAllProducts || !this.manager.state.productTemplate) ? state.switchingProduct : state.switchingProduct.filter(spe => spe.match)} parts={this.manager.state.parts} onSelect={(d, parts) => this.switchProduct(d.product, d.product_template, parts)} builder={this} />

						{!(state.showAllProducts || !this.manager.state.productTemplate) && <div>
							<div class="builder__product-switch-info">
								Showing products with same design available.
							</div>
							<div class="builder__product-switch-actions">
								<button class="builder__product-switch-action" onClick={() => this.setState({ showAllProducts: true })}>Show all products</button>
								<button class="builder__product-switch-action" onClick={() => this.saveAllDesigns()}>Save shown designs</button>
							</div>
						</div>}

					</div>
					{state.mobile && <div class="builder__floating-action builder__floating-action--hide-settings">
						<button class="builder__floating-action-button" onClick={() => this.setState({ settingsHidden: !state.settingsHidden })}>
							{state.settingsHidden ? <Icon name="arrow-up" /> : <Icon name="arrow-down" />}
							{state.settingsHidden == false && <span>Expand Preview</span>}
						</button>
					</div>}
				</div>}

				{(!state.dialog || !state.mobile) && <div key="settings" className={`builder__settings-column ${state.settingsHidden ? 'builder__settings-column--hidden' : ''}`}>
					{!state.mobile && <BuilderDesktopSettings parts={this.manager.state.partsSorted} builder={this} product={props.product} flow={props.flow} selected={state.selectedPart} domain={props.domain} />}
					{state.mobile && <BuilderMobileSettings parts={this.manager.state.partsSorted} builder={this} product={props.product} flow={props.flow} selected={state.selectedPart} domain={props.domain} />}
					{!state.mobile && <CartBlock
						roster={this.manager.state.roster}
						priceBreakdown={state.priceBreakdown}
						addButtonHandler={() => this.manager.state.productTemplate ? this.saveToCart() : showAlert('Please select a design first', 'warning')}
						rosterButtonHandler={() => maybe(this.rosterUi?.base, c => (c as HTMLElement).scrollIntoView())}
						cartItem={props.cartItem}
						manager={this.manager}
					/>}
				</div>}

				{state.dialog && state.mobile && <div className="builder__mobile-popup">
					<div className="builder__mobile-popup-close" onClick={() => this.setState({ dialog: undefined })}>
						<Icon name="close" />
					</div>
					{state.dialog == 'roster' && [
						<h2>
							Roster
							<a href="/faq#jerseys">Learn More</a>
						</h2>,
						<RosterUI
							roster={this.manager.state.roster}
							previewing={this.manager.state.previewingRoster}
							onChange={this.manager.updateRosterEntry}
							onDelete={this.manager.deleteRosterEntry}
							onPreview={this.manager.previewRosterEntry}
							onDone={() => this.setState({ dialog: undefined })}
							product={props.product}
							fields={rosterFields}
							showPart={(type: RosterPartType) => {
								this.setState({ dialog: undefined });
								this.rosterEditorShowPart(type);
							}}
							replace={this.manager.replaceRoster}
							showSizeCharts={() => this.setState({ dialog: 'sizeCharts' })}
							sizesInStock={this.manager.state.productTemplateInventory?.productSizesInStock}
							sizesOfferedByVendor={this.manager.state.productTemplateInventory?.productSizesFromVendor}
						/>,
					]}
					{state.dialog == 'share' && <ShareDialog builder={this} url={state.shareUrl} canSave={!!this.manager.state.productTemplate} />}
					{state.dialog == 'artwork' && <ArtworkBrowser builder={this} />}
					{state.dialog == 'auth' && <div class="builder__auth">
						<AuthForm
							initTitle={<>
								<span>Sign up or log in to {this.props.context.domain.brand_name}</span>{' '}
								<span>to save and share your designs</span>
							</>}
							targetUrl={state.shareUrl}
							availableServices={props.availableAuthServices}
						/>
						<div class="builder__auth-continue">
							<button onClick={() => this.setState({ dialog: undefined })}>Continue as guest</button>
						</div>
					</div>}
				</div>}

			</Fragment>

			{state.dialog && !state.mobile && <Popup close={() => this.setState({ dialog: undefined })}>
				{state.dialog == 'share' && <ShareDialog builder={this} url={state.shareUrl} canSave={!!this.manager.state.productTemplate} />}
				{state.dialog == 'artwork' && <ArtworkBrowser builder={this} />}
				{state.dialog == 'auth' && <div class="builder__auth">
					<AuthForm
						initTitle={<>
							<span>Sign up or log in to {this.props.context.domain.brand_name}</span>{' '}
							<span>to save and share your designs</span>
						</>}
						targetUrl={state.shareUrl}
						availableServices={props.availableAuthServices}
					/>
					<div class="builder__auth-continue">
						<button onClick={() => this.setState({ dialog: undefined })}>Continue as guest</button>
					</div>
				</div>}
			</Popup>}
			{state.dialog == 'sizeCharts' && <Popup close={() => this.setState({ dialog: undefined })}>
				<SizeChartTable product={props.product} />
			</Popup>}

		</main>;

	}

	init() {

		let parts: Maybe<DesignParts>;
		let roster: RosterEntry[] | undefined;
		let previewingRoster: string | undefined = undefined;

		const q = querystring.parse(location.hash.substr(1));
		let initJson: string | undefined;
		try {
			const b64 = q.state as (string | undefined);
			initJson = b64 && pako.inflate(Buffer.from(b64, 'base64'), { to: 'string' });
		}
		catch (e) {
			console.error(e);
		}

		if (q.experiments) {
			localStorage.builderExperiments = q.experiments;
		}

		maybe(
			localStorage.builderExperiments,
			(str: string) => this.experiments = new Set(str.split(',').map(s => s.trim()).filter(s => !!s))
		);
		
		const {
			design,
			product,
			flow,
			sharedItem,
			initProductTemplate,
			context,
			cartItem,
			productTemplates,
		} = this.props;
		
		const item = cartItem || sharedItem;
		if (item && !initJson) {
			parts = ensure(design).parts;
			roster = upgradeRoster(item.roster || []);
		}
		else {

			if (!initJson) initJson = localStorage[`builderState_${product.id}`];
			if (product.id == 'jersey' && !initJson) initJson = localStorage.builderState;

			let stored: any;
			try {
				if (initJson) stored = JSON.parse(initJson);
			}
			catch (e) {
				console.error(e);
			}

			if (stored && (!stored.version || parseInt(stored.version) < this.version || !!initProductTemplate)) stored = undefined;
			parts = maybe(stored?.parts, p => Array.isArray(p) ? makeIndex(p) : p);
			roster = upgradeRoster(stored?.roster || [{}]);

			this.state = {
				...this.state,
				logoColors: stored?.logoColors,
				featuredColor: stored?.featuredColor,
				secondaryColor: stored?.secondaryColor,
			};

		}

		previewingRoster = maybePipe(
			parts,
			Object.values,
			list => rosterTextPartTypes.filter(pt => Object.values(list).some(p => p.type == pt)),
			types => roster.find(re => types.some(pt => !!re[pt]?.length))
		)?.id;
		const productTemplate = maybe(
			initProductTemplate ?? maybe(parts, getProductTemplateFromParts),
			ptid => productTemplates.find(pd => pd.id == ptid)
		);
		
		const productTemplateInventory = productTemplate?.id && this.props.productTemplateInventory.find(pti => pti.id === productTemplate.id) || undefined;

		this.manager = new DesignManager({
			domain: context.domain.id,
			...pick(this.props, [
				'product',
				'flow',
				'logoInDesign',
				'threadColors',
				'productTemplates',
				'productTemplateInventory',
			]),
			state: {
				parts: parts ?? undefined,
				roster,
				productTemplate,
				productTemplateInventory,
				previewingRoster,
			},
		});
		this.manager.addListener(() => {
			this.updatePrice();
			this.save();
			this.forceUpdate();
			this.updateZoomArea();
		});
		this.manager.calculateLogoColors = this.calculateLogoColors;

		this.manager.processAllLogos();
		this.updatePrice();

		if (q.add_upload) {
			this.manager.fetchAndAddUpload(q.add_upload as string);
			location.hash = '';
		}

		const type = maybe(q.add_part, qt => isIn(qt, rosterTextPartTypes) ? qt : null) ?? null;
		const part = type && (isIn(type, textPartTypes) ? this.manager.addTextPart(type) : type == 'flag' && this.manager.product.features?.flag ? this.manager.ensureFlag() : null) || null;

		if (type && part) {
			const { roster } = this.manager.state;
			this.manager.applyRosterEntry(roster.find(re => !!re[type as keyof RosterEntry]) ?? roster[0]);
			this.rosterEditorShowPart(type, part);
		}

		const profile = flow.color_profile;
		if (profile) getColorPresetsForProfile({ profile }).then(({ colorPresets }) => this.setState({ colorPresets })).catch(logRejection);

	}

	closeDialog() {
		this.setState({ dialog: undefined });
	}


	serialize({ parts = this.manager.state.parts, lockColors = true }: { parts?: DesignParts; lockColors?: boolean } = {}) {

		const json = JSON.stringify({
			version: this.version,
			parts: this.manager.exportParts({ parts, lockColors }),
			roster: this.manager.state.roster,
			previewingRoster: this.manager.state.previewingRoster,
			logoColors: this.state.logoColors,
			featuredColor: this.state.featuredColor,
			secondaryColor: this.state.secondaryColor,
		});

		const zipped = pako.deflate(json);
		const b64 = Buffer.from(zipped).toString('base64');

		return { json, b64 };

	}

	save = throttle(() => {

		const { json, b64 } = this.serialize({ lockColors: false });

		localStorage[`builderState_${this.props.product.id}`] = json;
		history.replaceState({}, document.title, '#state=' + encodeURIComponent(b64));

	}, 500);

	isMobile() {
		return window.innerWidth <= 800 || window.innerHeight <= 400;
	}
	
	makeShareUrl(sdid: string) {
		return `https://${this.props.context.domain.host}/builder/?saved=${sdid}`;
	}

	startDrag(cb: (cx: number, cy: number) => void, end?: () => void) {
		this.doDrag = cb;
		this.endDrag = end;
	}

	toggleSide = () => {
		this.setState({ side: this.state.side == 'front' ? 'back' : 'front' });
	};
	toggleInvert() {
		this.setState({ invertDesignPreview: !this.state.invertDesignPreview });
	}

	switchProductTemplate(dir: number) {
		const designs = this.props.productTemplates;
		const cur = designs.findIndex(t => t.id == this.manager.state.productTemplate!.id);
		const idx = (cur + dir + designs.length) % designs.length;
		this.manager.setProductTemplate(designs[idx]);
	}

	selectPart = (pid: Maybe<string>) => {
		this.setState({ selectedPart: pid }, this.updateZoomArea);
	};

	updateZoomArea = (enable = false) => {
		const sa = this.designScrollArea;
		if (!(enable || this.state.zoomArea) || !sa || !this.state.selectedPart) return this.resetZoom();
		const part = this.manager.getPart(this.state.selectedPart);
		const point = part?.point;
		if (!part || !point) return this.resetZoom();
		const is = ensure(this.props.product.features?.imageSizes?.[0]);
		const sr = sa.getBoundingClientRect();
		const ratio = is[0] / is[1];
		const pw = ensure(point.w) / ratio;
		let width, height;
		if ((sr.width / sr.height) < ratio) {
			width = Math.round(Math.min(2048, sr.width / pw - 32));
			height = Math.round(width / ratio);
		}
		else {
			height = Math.round(Math.min(2048, sr.height / pw - 32));
			width = Math.round(height * ratio);
		}
		this.setState({ zoomArea: {
			part: part.id,
			side: ensure(point.side),
			point,
			width,
			height,
			panX: Math.round(-width * (0.5 + (point.x || 0) / ratio) + (sr.width / 2)),
			panY: Math.round(-height * (0.5 + (point.y || 0)) + (sr.height / 2)),
		} });
	};
	resetZoom = () => this.setState({ zoomArea: null });

	updatePrice = throttle(async () => {
		const res = await calculateDesignPrice({
			product: this.props.product.id,
			parts: this.manager.exportParts(),
			roster: this.manager.state.roster,
		});
		if (JSON.stringify(res.breakdown) == JSON.stringify(this.state.priceBreakdown)) return;
		this.setState({ priceBreakdown: res.breakdown });
	}, 200);

	async savePreview() {

		if (!this.manager.state.productTemplate) {
			return await showAlert('Please select a design first', 'warning');
		}
		
		const saved = await this.saveDesign(true);
		if (!saved) return;
		const { id, design } = saved;
		
		if (!this.props.haveUser) {
			this.setState({ dialog: 'auth', shareUrl: this.makeShareUrl(id) });
			return;
		}

		const url = makeDesignPreviewUrl(design.id, { previewWidth: 768, format: 'png' });

		const a = document.createElement('a');
		a.download = `${design.id}.png`;
		a.href = url;
		document.body.appendChild(a);
		a.click();

		setTimeout(function () {
			document.body.removeChild(a);
			URL.revokeObjectURL(url);
		}, 1000);

	}

	async showSharingPopup() {

		if (!this.manager.state.productTemplate) {
			return await showAlert('Please select a design first', 'warning');
		}

		if (!this.props.haveUser) {
			this.setState({ dialog: 'auth' });
		}
		else {
			this.setState({
				dialog: 'share',
				shareUrl: undefined,
			});
		}

		const product = this.props.product.id;
		const parts = this.manager.exportParts();
		const roster = this.manager.state.roster;

		const res = await saveDesign({
			previousDesign: this.props.design?.id,
			product,
			parts,
			roster,
		});
		if (this.props.haveUser && res.id) this.setState({
			shareUrl: this.makeShareUrl(res.id),
		});

	}

	async saveDesign(silent = false): Promise<Maybe<SaveDesignResult>> {

		if (!this.manager.state.productTemplate) {
			await showAlert('Please select a design first', 'warning');
			return null;
		}

		const product = this.props.product.id;
		const parts = this.manager.exportParts();
		const roster = this.manager.state.roster;

		const res = await saveDesign({
			previousDesign: this.props.design?.id,
			product,
			parts,
			roster,
		}).catch(e => showErrorAlert(e));
		if (!res) return null;

		if (res.id && !silent) {
			if (this.props.haveUser) await showAlert('Design saved to your profile', 'info');
			else this.setState({ dialog: 'auth', shareUrl: this.makeShareUrl(res.id) });
		}

		return res;

	}

	async saveAllDesigns() {
		const entries = this.state.switchingProduct!.filter(spe => spe.product_template.template == this.manager.state.productTemplate?.template);
		alert('Feature temporarily unavailable');
		// FIXME
		throw new Error('saveAllDesigns broken');
	}

	saveToCart = alertOnError(async () => {

		const saved = await this.saveDesign(true);
		if (!saved) return;
		const { design } = saved;

		const roster = this.manager.state.roster;

		let res: UpdateCartItemsResult | AddDesignToCartResult;
		if (this.props.cartItem) {
			res = await updateCartItems({
				items: [{
					id: this.props.cartItem.id,
					design: design.id,
					roster,
				}],
			});
		}
		else {
			res = await addDesignToCart({ design: design.id, roster });
		}

		const redirect = !this.state.mobile ? await showConfirm('Your item was successfully added to your cart!', ['View cart', 'Continue shopping'], { width: 500 }) : true;
		if (redirect) location.href = '/cart';
	});

	calculateLogoColors = async (part: DesignPart) => {

		const { colors } = await calculateUploadColors({
			upload: part.upload!.id,
			profile: this.props.flow.color_profile,
		});
				
		this.manager.updatePart(part.id, { upload: { ...part.upload!, colors } });

		ColorPicker.loadCustomColors();
		for (const color of colors.slice().reverse()) ensure(ColorPicker.customColors).unshift(color);
		ColorPicker.saveCustomColors();

		if (colors.length) this.setState({
			logoColors: colors,
			featuredColor: colors[0],
			secondaryColor: colors[1],
		});
		
		return colors;

	};

	async maybeReset() {

		if (!await showConfirm('Forget all your changes and reset the editor to the initial state?', ['Yes', 'No'])) return;

		this.manager.reset();
		this.setState({
			logoColors: undefined,
			featuredColor: undefined,
			secondaryColor: undefined,
			selectedPart: undefined,
		}, () => {
			this.updatePrice();
			this.manager.applyRosterEntry();
		});


		ColorPicker.customColors = [];
		ColorPicker.saveCustomColors();

		this.save();

	}

	browseArtwork() {
		this.setState({ dialog: 'artwork' });
	}

	showProductSwitch = alertOnError(async () => {

		if (this.state.switchingProduct) {
			this.setState({ switchingProduct: undefined, showAllProducts: false });
			return;
		}

		const { rows } = await listOtherApparelProducts({
			product: this.props.product.id,
			template: this.manager.state.productTemplate?.template,
		});
		
		this.setState({ switchingProduct: rows });

	});

	switchProduct(product: PublicProduct, design: ProductTemplate, parts: DesignParts) {
		if (product.id != this.props.product.id) {
			const { b64 } = this.serialize({ parts });
			location.href = '?product=' + product.id + '#state=' + encodeURIComponent(b64);
		}
		else {
			this.setState({ switchingProduct: undefined, showAllProducts: false });
		}
	}

	async startSocialAuth(method: string) {
		const after = location.href;
		document.cookie = `authRedir=${encodeURIComponent(after)}; path=/`;
		const res = await startSocialAuth({ method });
		if (res.redir) location.href = res.redir;
	}

	rosterEditorShowPart(type: RosterPartType, part = findMatch(this.manager.state.partsSorted, { type })) {
		if (type === 'flag') {
			if (!part) return;
			this.manager.updatePart(part.id, { flag: { ...part.flag, left: 'on' } });
			this.setState({ side: 'front' });
		}
		else {
			if (!part) part = this.manager.addTextPart(type);
			this.setState({ side: part.point?.side || 'back' });
		}
		this.selectPart(part.id);
		this.setState({ settingsHidden: false });
	}
}

export const DesignGrid = ({ side, parts, productTemplates, productTemplateInventory, onSelect, builder }: {
	side: string;
	parts: DesignPart[];
	productTemplates: ProductTemplate[];
	productTemplateInventory: ProductTemplateInventory[];
	onSelect: (t: ProductTemplate, i: Maybe<ProductTemplateInventory>) => void;
	builder: Builder;
}) => <div className="builder__design-grid">
	{productTemplates.map(d => <div className={`builder__design-grid-cell ${maybe(productTemplateInventory.find(pti => pti.id === d.id)?.productSizesInStock, s => !s.length) && 'builder__design-grid-cell-out-of-stock'}`}>
		<DesignPreview
			product={builder.props.product}
			flow={builder.props.flow}
			parts={makeIndex(parts.map(part => {
				if (part.type == 'apparel') return { ...part, productTemplate: d };
				if (!part.position || part.position == 'custom') return part;
				const clone = { ...part };
				builder.manager.assignDefaultPoint(clone, d);
				return clone;
			}))}
			builder={builder}
			side={side}
			light={true}
			onClick={() => onSelect(d, productTemplateInventory.find(pti => pti.id === d.id))}
		/>
		<div className="builder__design-grid-info">
			<div className="builder__design-grid-name">
				{d.sanmar_color_name ? [d.sanmar_color_name, <br />] : ''}
			</div>
		</div>
	</div>)}
</div>;

export const ProductSwitchGrid = ({ data, parts, onSelect, builder }: {
	data: ProductSwitchEntry[];
	parts: DesignParts;
	onSelect: (d: ProductSwitchEntry, op: DesignParts) => void;
	builder: Builder;
}) => <div className="builder__design-grid">
	{data.map(entry => {
		const op = builder.manager.adaptPartsToProduct(parts, entry.product, entry.product_template);
		return <div className={`builder__design-grid-cell ${maybe(entry.product_template_inventory.productSizesInStock, s => !s.length) && 'builder__design-grid-cell-out-of-stock'}`}>
			<DesignPreview
				product={entry.product}
				flow={entry.flow}
				parts={op}
				builder={builder}
				side={'front'}
				light={true}
				onClick={() => onSelect(entry, op)}
			/>
			<div className="builder__design-grid-info">
				<div className="builder__design-grid-name">
					{entry.product.name}
					{builder.props.isAdmin && <div>{entry.product.id}</div>}
				</div>
				<div className="builder__design-grid-price">
					{
						entry.product.min_order_quantity
							? `${formatPrice(ensure(entry.product.moq_price))} / ${entry.product.min_order_quantity} pcs`
							: formatPrice(entry.product.price)
					}
				</div>
			</div>
		</div>;

	})}
</div>;

function ShareDialog({ builder, url, canSave }: { builder: Builder; url?: string; canSave: boolean }) {
	let input: HTMLInputElement;
	return <div className="builder-share">
		<span>{url ? 'Share this link with your friends' : 'Preparing link...'}</span>
		<div className="builder-share__link">
			<input className="builder-share__link-input" value={url} ref={instance => input = instance!} readOnly={true} />
			<button onClick={() => {
				input.focus();
				input.select();
				document.execCommand('copy');
			}}>
				<Icon name="copy" />
			</button>
		</div>
		{canSave && <button className="builder-share__download" onClick={() => builder.savePreview()}>
			<Icon name="image-file" />
			Save preview
		</button>}
	</div>;
}
